import { normalizeFinding, validateFindingDetails } from "./findingFormRules";
import { getUsersList } from "../../helpers/backend_helper";
import React, { useEffect, useRef, useState } from "react";

import { getData, postData, postRawData } from "../../helpers/url-request";
import FindingEditorModal from "./FindingEditorModal";

export default function ObservationFindingConversion({
  inspectionId,
  observation,
  onClose,
  onSaved,
}) {
  const [value, setValue] = useState({
    title: "",
    description: observation.description,
    category: "",
    severity: "",
    rootCause: "",
    recommendation: "",
    evidenceIds: [],
    managementResponseRequired: "no",
    responseFrom: "",
    responseDueDate: "",
    deficiencyExists: false,
  });
  const [configuration, setConfiguration] = useState(null);
  const [assignees, setAssignees] = useState([]);
  useEffect(() => {
    let active = true;
    getUsersList()
      .then((response) => {
        if (active && response?.status)
          setAssignees(
            (response.data || [])
              .filter((user) => user.isBlocked !== 1)
              .map((user) => ({
                id: user.id,
                name: user.fullName || user.userName || user.emailId,
              }))
          );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const upload = useRef(null);
  const attach = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    setUploading(true);
    try {
      for (const file of selected) {
        const form = new FormData();
        form.append("document", file);
        form.append("findingAttachment", "true");
        form.append(
          "inspectionChecklistId",
          String(observation.questionId || 0)
        );
        const response = await postRawData(
          `/inspection/${inspectionId}/evidence`,
          "POST",
          form
        );
        if (!response?.status)
          throw new Error(response?.message || "Unable to upload attachment.");
        setFiles((current) => [...current, response.data]);
        setValue((current) => ({
          ...current,
          evidenceIds: [...current.evidenceIds, response.data.id],
        }));
      }
    } catch (failure) {
      setErrors({ submission: failure.message });
    } finally {
      setUploading(false);
    }
  };
  useEffect(() => {
    getData(`/inspection/${inspectionId}/finding-configuration`)
      .then((response) => {
        if (response?.status) setConfiguration(response.data);
      })
      .catch(() => {});
  }, [inspectionId]);
  const save = async () => {
    const finding = normalizeFinding(value);
    const validation = validateFindingDetails(
      finding,
      new Set(
        (configuration?.requiredFields || []).map((field) =>
          String(field).replaceAll(" ", "").toLowerCase()
        )
      )
    );
    if (
      finding.deficiencyExists === true &&
      (configuration?.requiredFields || []).some(
        (field) => String(field).toLowerCase() === "recommendation"
      ) &&
      !finding.recommendation?.trim()
    )
      validation.recommendation = "Recommendation is required.";
    if (
      finding.deficiencyExists === true &&
      finding.managementResponseRequired === "yes"
    ) {
      if (!finding.responseFrom)
        validation.responseFrom = "Select who needs to respond.";
      if (!finding.responseDueDate)
        validation.responseDueDate = "A response due date is required.";
    }
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      const responseFrom = finding.responseFrom || "";
      const response = await postData(`/inspection/${inspectionId}/findings`, {
        ...finding,
        observationId: observation.id,
        questionId: observation.questionId,
        sourceType: observation.questionId
          ? "ChecklistQuestion"
          : "GeneralEngagement",
        managementResponseRequired:
          finding.managementResponseRequired === "yes",
        responseRequestedFromRole: responseFrom.startsWith("role:")
          ? responseFrom.slice(5)
          : null,
        responseRequestedFromUserId: responseFrom.startsWith("user:")
          ? Number(responseFrom.slice(5))
          : null,
        responseDueDate: finding.responseDueDate || null,
        closureApprovalRequired: finding.closureApprovalRequired === "yes",
        remedialActionsDate: finding.remedialActionsDate || null,
        findingResolvedDate:
          finding.findingResolvedDate ||
          (finding.findingResolved ? finding.remedialActionsDate : null) ||
          null,
      });
      if (!response?.status)
        throw new Error(response?.message || "Unable to convert observation.");
      onSaved(response.data);
    } catch (failure) {
      setErrors({ submission: failure.message });
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <input ref={upload} type="file" multiple hidden onChange={attach} />
      <FindingEditorModal
        mode="create"
        engagement="Current review"
        checklist="Observation conversion"
        question={
          observation.questionId
            ? `Question ${observation.questionId}`
            : "Review observation"
        }
        value={value}
        onChange={setValue}
        configuration={configuration}
        requiredFields={
          new Set(
            (configuration?.requiredFields || []).map((field) =>
              String(field).replaceAll(" ", "").toLowerCase()
            )
          )
        }
        evidenceFiles={files}
        assignees={assignees}
        errors={errors}
        onErrors={setErrors}
        onBrowseFiles={() => upload.current?.click()}
        onRemoveEvidence={(id) => {
          setFiles((current) => current.filter((file) => file.id !== id));
          setValue((current) => ({
            ...current,
            evidenceIds: current.evidenceIds.filter((item) => item !== id),
          }));
        }}
        onClose={onClose}
        onSave={save}
        saving={saving}
        evidenceUploading={uploading}
      />
    </>
  );
}
